import * as React from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { gettext } from "i18n";
import apiClient from "api";
import { useAuth, useCurrentUser, useLogout } from "state/auth";
import { RESTRICTION_LOCATION, resendLocationEmail } from "utils/mfa";

// How often the page checks whether the login has been confirmed
const POLL_INTERVAL_MS = 5000;

/**
 * Shown while the login waits for the user to confirm it from the link emailed
 * to them (a login from an unusual place). Carries on by itself once confirmed,
 * e.g. from the user's phone.
 */
const LocationPendingPage: React.FC = () => {
  const { user } = useAuth();
  const logout = useLogout();
  const retrieveCurrentUser = useCurrentUser(true);
  // The latest, for the timer started once
  const retrieveRef = React.useRef(retrieveCurrentUser);
  retrieveRef.current = retrieveCurrentUser;

  React.useEffect(() => {
    const timer = window.setInterval(async () => {
      // A new JWT tells whether the session still waits
      apiClient.discardAuthJWT();
      const result = await retrieveRef.current();
      if (result.user?.restriction !== RESTRICTION_LOCATION) {
        window.clearInterval(timer);
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  const resend = async () => {
    try {
      await resendLocationEmail();
      toast.success(gettext("A new email was sent"));
    } catch (e) {
      toast.error(gettext((e as Error).message));
    }
  };

  return (
    <div className="LocationPendingPage">
      <h4>{gettext("Confirm it's you")}</h4>
      <p>
        {gettext(
          "You are logging in from a place your account is not usually used from. To keep your account safe, we sent a confirmation link to %1.",
          user?.email ?? "",
        )}
      </p>
      <p>
        {gettext(
          "Open the link, on this or another device, and confirm the login. This page continues by itself once you have.",
        )}
      </p>
      <p className="text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        {gettext("Waiting for confirmation...")}
      </p>
      <div className="d-flex gap-2">
        <Button variant="outline-secondary" onClick={resend}>
          {gettext("Send the email again")}
        </Button>
        <Button variant="link" onClick={logout}>
          {gettext("Log out")}
        </Button>
      </div>
    </div>
  );
};

export default LocationPendingPage;
